/**
 * @fileoverview Zustand Store para gerenciamento de estado da DRE editável
 * @module stores/dreStore
 * 
 * Store principal para edição de DRE com:
 * - Carregamento/salvamento no Supabase
 * - Edição de campos
 * - Adição/remoção de anos
 * - Recálculo automático
 * - Validações
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import toast from 'react-hot-toast';

import type { DREData, DREYear, ValidationState } from '../types/dre';
import { createEmptyDREYear } from '../types/dre';
import { calculateDREFields, recalculateDREYear, validateDREYears } from '../lib/dreCalculations';
import { supabase } from '../lib/supabase';
import { compressData, decompressData, getCompressionStats } from '../lib/compression';
import { MASTER_DRE_DATA } from '../lib/dre/masterData';
import { useUndoRedoStore } from './undoRedoStore';
import { store } from '../store'; // ✅ ANOS DINÂMICOS: Import Redux store
import { updateBaseYearFromDRE } from '../store/slices/valuationSlice'; // ✅ ANOS DINÂMICOS: Import action

// ============================================================================
// CONFIGURAÇÃO
// ============================================================================

// 🟢 MODO ONLINE: Habilitar persistência no Supabase
const OFFLINE_MODE = false;

// 🧪 MODO DEV: Permitir salvar sem autenticação (APENAS PARA TESTES)
// ⚠️ Mude para false em produção!
const DEV_MODE_ALLOW_UNAUTHENTICATED = true;
const DEV_USER_ID = '00000000-0000-0000-0000-000000000001'; // UUID fixo para testes

// ⏱️ Debounce timer para auto-save (evitar salvar a cada keystroke)
let autoSaveTimer: NodeJS.Timeout | null = null;
const AUTO_SAVE_DELAY = 2000; // 2 segundos

// ============================================================================
// HELPER: Sincronizar baseYear com Redux
// ============================================================================

/**
 * ✅ ANOS DINÂMICOS: Atualiza Redux baseYear quando DRE muda
 * 
 * Esta função dispara a action updateBaseYearFromDRE no Redux store
 * para sincronizar o baseYear com o último ano da DRE.
 * 
 * Deve ser chamado após:
 * - Carregar DRE do Supabase
 * - Adicionar novo ano
 * - Remover ano
 * - Qualquer mudança que afete os anos disponíveis
 */
function syncBaseYearWithRedux(dreData: DREData | null) {
  if (!dreData) {
    console.warn('[dreStore] ⚠️ Nenhuma DRE para sincronizar baseYear');
    return;
  }
  
  console.log('[dreStore] 🔄 Sincronizando baseYear com Redux...');
  
  // Dispatch action para Redux
  store.dispatch(updateBaseYearFromDRE({
    setima: { years: dreData.setima.years },
    studio555: { years: dreData.studio555.years }
  }));
  
  console.log('[dreStore] ✅ BaseYear sincronizado com Redux');
}

// ============================================================================
// TIPOS
// ============================================================================

interface DREState {
  // Estado
  dreData: DREData | null;
  masterData: Omit<DREData, 'id' | 'userId' | 'createdAt' | 'updatedAt'> | null;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  validationState: ValidationState | null;
  
  // Actions - Carregamento
  loadDRE: () => Promise<void>;
  loadMasterVersion: () => Promise<void>;
  initializeDefaultDRE: () => Promise<void>;
  
  // Actions - Edição
  setEditing: (editing: boolean) => void;
  updateField: (company: 'setima' | '555', year: number, field: string, value: number) => void;
  
  // Actions - Anos
  addYear: (company: 'setima' | '555', year: number) => void;
  removeYear: (company: 'setima' | '555', year: number) => void;
  
  // Actions - Salvamento
  saveDRE: () => Promise<void>;
  
  // Actions - Recálculo
  recalculateAll: () => void;
  
  // Actions - Validação
  validate: () => void;
  
  // Actions - Undo/Redo
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  
  // Actions - Reset
  reset: () => void;
  restoreToOriginal: () => void;
}

// ============================================================================
// DADOS PADRÃO (baseado no histórico real)
// ============================================================================

const DEFAULT_SETIMA_YEARS: DREYear[] = [
  {
    year: 2020,
    receitaBruta: 4175000,
    impostosVendas: 688275,
    custosDirectos: 1670000,
    despesasOperacionais: 1000000,
    depreciacao: 50000,
    impostosLucro: 250000,
    // Calculados
    receitaLiquida: 3486725,
    lucroBruto: 1816725,
    margemBruta: 52.1,
    ebitda: 816725,
    margemEbitda: 23.4,
    ebit: 766725,
    margemEbit: 22.0,
    lucroLiquido: 516725,
    margemLiquida: 14.8,
  },
  {
    year: 2021,
    receitaBruta: 5525000,
    impostosVendas: 911125,
    custosDirectos: 2210000,
    despesasOperacionais: 1400000,
    depreciacao: 75000,
    impostosLucro: 300000,
    // Calculados
    receitaLiquida: 4613875,
    lucroBruto: 2403875,
    margemBruta: 52.1,
    ebitda: 1003875,
    margemEbitda: 21.8,
    ebit: 928875,
    margemEbit: 20.1,
    lucroLiquido: 628875,
    margemLiquida: 13.6,
  },
  {
    year: 2022,
    receitaBruta: 8875000,
    impostosVendas: 1464375,
    custosDirectos: 3550000,
    despesasOperacionais: 2200000,
    depreciacao: 100000,
    impostosLucro: 500000,
    // Calculados
    receitaLiquida: 7410625,
    lucroBruto: 3860625,
    margemBruta: 52.1,
    ebitda: 1660625,
    margemEbitda: 22.4,
    ebit: 1560625,
    margemEbit: 21.1,
    lucroLiquido: 1060625,
    margemLiquida: 14.3,
  },
  {
    year: 2023,
    receitaBruta: 11800000,
    impostosVendas: 1947000,
    custosDirectos: 4720000,
    despesasOperacionais: 3000000,
    depreciacao: 150000,
    impostosLucro: 650000,
    // Calculados
    receitaLiquida: 9853000,
    lucroBruto: 5133000,
    margemBruta: 52.1,
    ebitda: 2133000,
    margemEbitda: 21.6,
    ebit: 1983000,
    margemEbit: 20.1,
    lucroLiquido: 1333000,
    margemLiquida: 13.5,
  },
  {
    year: 2024,
    receitaBruta: 15500000,
    impostosVendas: 2557500,
    custosDirectos: 6200000,
    despesasOperacionais: 3800000,
    depreciacao: 200000,
    impostosLucro: 850000,
    // Calculados
    receitaLiquida: 12942500,
    lucroBruto: 6742500,
    margemBruta: 52.1,
    ebitda: 2942500,
    margemEbitda: 22.7,
    ebit: 2742500,
    margemEbit: 21.2,
    lucroLiquido: 1892500,
    margemLiquida: 14.6,
  },
];

const DEFAULT_555_YEARS: DREYear[] = [
  {
    year: 2020,
    receitaBruta: 600000,
    impostosVendas: 99000,
    custosDirectos: 240000,
    despesasOperacionais: 150000,
    depreciacao: 20000,
    impostosLucro: 30000,
    // Calculados
    receitaLiquida: 501000,
    lucroBruto: 261000,
    margemBruta: 52.1,
    ebitda: 111000,
    margemEbitda: 22.2,
    ebit: 91000,
    margemEbit: 18.2,
    lucroLiquido: 61000,
    margemLiquida: 12.2,
  },
  {
    year: 2021,
    receitaBruta: 785000,
    impostosVendas: 129525,
    custosDirectos: 314000,
    despesasOperacionais: 200000,
    depreciacao: 25000,
    impostosLucro: 40000,
    // Calculados
    receitaLiquida: 655475,
    lucroBruto: 341475,
    margemBruta: 52.1,
    ebitda: 141475,
    margemEbitda: 21.6,
    ebit: 116475,
    margemEbit: 17.8,
    lucroLiquido: 76475,
    margemLiquida: 11.7,
  },
  {
    year: 2022,
    receitaBruta: 1240000,
    impostosVendas: 204600,
    custosDirectos: 496000,
    despesasOperacionais: 310000,
    depreciacao: 35000,
    impostosLucro: 65000,
    // Calculados
    receitaLiquida: 1035400,
    lucroBruto: 539400,
    margemBruta: 52.1,
    ebitda: 229400,
    margemEbitda: 22.2,
    ebit: 194400,
    margemEbit: 18.8,
    lucroLiquido: 129400,
    margemLiquida: 12.5,
  },
  {
    year: 2023,
    receitaBruta: 1650000,
    impostosVendas: 272250,
    custosDirectos: 660000,
    despesasOperacionais: 420000,
    depreciacao: 45000,
    impostosLucro: 85000,
    // Calculados
    receitaLiquida: 1377750,
    lucroBruto: 717750,
    margemBruta: 52.1,
    ebitda: 297750,
    margemEbitda: 21.6,
    ebit: 252750,
    margemEbit: 18.3,
    lucroLiquido: 167750,
    margemLiquida: 12.2,
  },
  {
    year: 2024,
    receitaBruta: 2170000,
    impostosVendas: 358050,
    custosDirectos: 868000,
    despesasOperacionais: 540000,
    depreciacao: 60000,
    impostosLucro: 115000,
    // Calculados
    receitaLiquida: 1811950,
    lucroBruto: 943950,
    margemBruta: 52.1,
    ebitda: 403950,
    margemEbitda: 22.3,
    ebit: 343950,
    margemEbit: 19.0,
    lucroLiquido: 228950,
    margemLiquida: 12.6,
  },
];

// ============================================================================
// STORE
// ============================================================================

// ============================================================================
// DRE STORE
// ============================================================================

export const useDREStore = create<DREState>()(
  immer((set, get) => ({
    // Estado inicial
    dreData: null,
    masterData: null,
    isEditing: false,
    isLoading: false,
    isSaving: false,
    validationState: null,

    // ========================================================================
    // CARREGAMENTO
    // ========================================================================

    /**
     * Carrega versão MASTER (read-only) do banco
     * Esta é a versão imutável com dados originais do Excel
     */
    loadMasterVersion: async () => {
      console.log('[dreStore] 🔵 loadMasterVersion() CHAMADO');
      try {
        // MODO OFFLINE: Usar dados locais primeiro para evitar erros 406
        console.log('[dreStore] 📌 Carregando versão Master (dados locais)...');
        set({ masterData: MASTER_DRE_DATA });
        console.log('[dreStore] ✅ MASTER_DRE_DATA definido no state');
        
        // 🔴 MODO OFFLINE ATIVADO - Pular chamadas Supabase completamente
        if (OFFLINE_MODE) {
          console.log('[dreStore] 🔴 OFFLINE MODE: Pulando atualização do Supabase');
          console.log('[dreStore] 🏁 loadMasterVersion() CONCLUÍDO (offline)');
          return;
        }
        
        // Tentar atualizar do banco em background (pode falhar silenciosamente)
        try {
          const { data, error } = await supabase
            .from('dre_master')
            .select('*')
            .single();

          if (!error && data) {
            // Atualizar com dados do banco se disponível
            set({
              masterData: {
                setima: {
                  companyId: 'setima',
                  companyName: 'Sétima',
                  years: data.setima_years as DREYear[],
                },
                studio555: {
                  companyId: '555',
                  companyName: '555 Studios',
                  years: data.studio555_years as DREYear[],
                },
              },
            });
            console.log('✅ Versão Master atualizada do banco');
          }
        } catch (bgError) {
          // Ignorar erro - já temos dados locais
          console.warn('⚠️ Não foi possível atualizar do banco, usando dados locais');
        }
      } catch (error) {
        // Fallback garantido para dados locais
        console.error('Erro ao carregar versão master:', error);
        set({ masterData: MASTER_DRE_DATA });
      }
    },

    loadDRE: async () => {
      console.log('[dreStore] 🔵 loadDRE() CHAMADO');
      set({ isLoading: true });
      console.log('[dreStore] ⏳ isLoading = true');
      
      try {
        // 1️⃣ STEP 1: Carregar dados locais PRIMEIRO (evita tela em branco)
        console.log('[dreStore] 📝 Carregando dados locais como baseline...');
        await get().initializeDefaultDRE();
        console.log('[dreStore] ✅ Baseline carregada com sucesso');
        
        // 2️⃣ STEP 2: Tentar sobrescrever com dados do Supabase (se ONLINE MODE)
        if (!OFFLINE_MODE) {
          // Tentar carregar do banco apenas se NÃO estiver em offline
          console.log('[dreStore] 📡 Tentando carregar do Supabase...');
          try {
            const { data: { user } } = await supabase.auth.getUser();
            
            if (!user) {
              console.warn('[dreStore] ⚠️ Usuário não autenticado, usando dados locais');
            } else {
              const { data, error } = await supabase
                .from('dre_data')
                .select('*')
                .eq('user_id', user.id)
                .single();

              if (!error && data) {
                // ✅ Verificar se há dados comprimidos (nova versão)
                let setimaYears: DREYear[];
                let studio555Years: DREYear[];

                if (data.data_compressed) {
                  // 🗜️ Descomprimir dados
                  console.log('[dreStore] 📦 Descomprimindo dados...');
                  const decompressed = decompressData<{
                    setima_years: DREYear[];
                    studio555_years: DREYear[];
                  }>(data.data_compressed);
                  
                  setimaYears = decompressed.setima_years;
                  studio555Years = decompressed.studio555_years;
                  console.log('[dreStore] ✅ Dados descomprimidos com sucesso');
                } else {
                  // 📝 Fallback para dados não comprimidos (versão antiga)
                  console.log('[dreStore] 📝 Usando dados não comprimidos (versão antiga)');
                  setimaYears = data.setima_years;
                  studio555Years = data.studio555_years;
                }

                // Atualizar com dados do banco se disponível
                set({
                  dreData: {
                    id: data.id,
                    userId: data.user_id,
                    setima: {
                      companyId: 'setima',
                      companyName: 'Sétima',
                      years: setimaYears,
                    },
                    studio555: {
                      companyId: '555',
                      companyName: '555 Studios',
                      years: studio555Years,
                    },
                    createdAt: new Date(data.created_at),
                    updatedAt: new Date(data.updated_at),
                  },
                });
                console.log('[dreStore] ✅ DRE atualizada do banco');
              }
            }
          } catch (bgError) {
            // Ignorar erro - já temos dados locais
            console.warn('[dreStore] ⚠️ Não foi possível atualizar do banco, usando dados locais');
          }
        } else {
          console.log('[dreStore] 🔴 OFFLINE MODE: Pulando carregamento do Supabase');
        }
      } catch (error) {
        console.error('[dreStore] ❌ Erro ao carregar DRE:', error);
        // Fallback garantido para dados padrão
        await get().initializeDefaultDRE();
      } finally {
        console.log('[dreStore] 🏁 isLoading = false');
        set({ isLoading: false });
        
        // ✅ ANOS DINÂMICOS: Sincronizar baseYear com Redux após carregar
        syncBaseYearWithRedux(get().dreData);
      }
    },

    initializeDefaultDRE: async () => {
      console.log('[dreStore] 🔵 initializeDefaultDRE() CHAMADO');
      // 🔴 OFFLINE MODE: Não chamar Supabase
      const userId = OFFLINE_MODE ? 'local-offline' : (await supabase.auth.getUser()).data?.user?.id || 'local';
      console.log('[dreStore] 👤 userId:', userId);
      
      const dreData: DREData = {
        id: crypto.randomUUID(),
        userId,
        setima: {
          companyId: 'setima',
          companyName: 'Sétima',
          years: DEFAULT_SETIMA_YEARS.map(recalculateDREYear),
        },
        studio555: {
          companyId: '555',
          companyName: '555 Studios',
          years: DEFAULT_555_YEARS.map(recalculateDREYear),
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      console.log('[dreStore] 💾 Definindo dreData no state...');
      set({ dreData });
      console.log('[dreStore] ✅ dreData definido');
      
      // ⚠️ NÃO salvar automaticamente! Salvamento só acontece quando usuário clica em "Salvar"
      // Isso evita criar registros duplicados no banco antes de carregar os dados reais
      console.log('[dreStore] 🔴 NÃO salvando automaticamente (apenas fallback local)');
      
      console.log('[dreStore] 🏁 initializeDefaultDRE() CONCLUÍDO');
    },

    // ========================================================================
    // EDIÇÃO
    // ========================================================================

    setEditing: (editing) => {
      set({ isEditing: editing });
    },

    updateField: (company, year, field, value) => {
      const state = get();
      
      // Salvar estado atual no histórico ANTES da mudança
      if (state.dreData) {
        const companyName = company === 'setima' ? 'Sétima' : '555 Studios';
        useUndoRedoStore.getState().pushAction(
          state.dreData,
          `Editou ${field} em ${year} (${companyName})`
        );
      }

      set((state) => {
        if (!state.dreData) return;

        const companyData = company === 'setima' ? state.dreData.setima : state.dreData.studio555;
        const yearData = companyData.years.find((y) => y.year === year);

        if (!yearData) return;

        // Atualizar campo editável
        (yearData as Record<string, number>)[field] = value;

        // ✅ MUDANÇA: Passa o campo editado para lógica bidirecional
        const calculated = calculateDREFields(yearData, field);
        Object.assign(yearData, calculated);

        // Atualizar timestamp
        state.dreData.updatedAt = new Date();
      });

      // Validar após mudança
      get().validate();
      
      // 💾 Auto-save com debounce (2 segundos após última edição)
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
      
      autoSaveTimer = setTimeout(() => {
        get().saveDRE();
        autoSaveTimer = null;
      }, AUTO_SAVE_DELAY);
    },

    // ========================================================================
    // ANOS
    // ========================================================================

    addYear: (company, year) => {
      const state = get();
      
      // Verificar antes de salvar no histórico
      if (state.dreData) {
        const companyData = company === 'setima' ? state.dreData.setima : state.dreData.studio555;
        if (companyData.years.some((y) => y.year === year)) {
          toast.error(`Ano ${year} já existe`);
          return;
        }

        // Salvar no histórico
        const companyName = company === 'setima' ? 'Sétima' : '555 Studios';
        useUndoRedoStore.getState().pushAction(
          state.dreData,
          `Adicionou ano ${year} (${companyName})`
        );
      }

      set((state) => {
        if (!state.dreData) return;

        const companyData = company === 'setima' ? state.dreData.setima : state.dreData.studio555;

        // 🆕 Buscar ano anterior para estimar depreciação
        const sortedYears = [...companyData.years].sort((a, b) => a.year - b.year);
        const previousYear = sortedYears[sortedYears.length - 1]; // Último ano existente
        
        // Criar novo ano (com depreciação estimada se possível)
        const newYear = createEmptyDREYear(year, previousYear, company);
        companyData.years.push(newYear);
        
        // Ordenar por ano
        companyData.years.sort((a, b) => a.year - b.year);

        // Atualizar timestamp
        state.dreData.updatedAt = new Date();
      });

      toast.success(`Ano ${year} adicionado`);
      
      // ✅ ANOS DINÂMICOS: Sincronizar baseYear com Redux
      syncBaseYearWithRedux(get().dreData);
      
      // 💾 Auto-save após adicionar ano
      get().saveDRE();
    },

    removeYear: (company, year) => {
      const state = get();
      
      // Salvar no histórico ANTES de remover
      if (state.dreData) {
        const companyName = company === 'setima' ? 'Sétima' : '555 Studios';
        useUndoRedoStore.getState().pushAction(
          state.dreData,
          `Removeu ano ${year} (${companyName})`
        );
      }

      set((state) => {
        if (!state.dreData) return;

        const companyData = company === 'setima' ? state.dreData.setima : state.dreData.studio555;
        const index = companyData.years.findIndex((y) => y.year === year);

        if (index === -1) return;

        companyData.years.splice(index, 1);
        
        // Atualizar timestamp
        state.dreData.updatedAt = new Date();
      });

      toast.success(`Ano ${year} removido`);
      
      // ✅ ANOS DINÂMICOS: Sincronizar baseYear com Redux
      syncBaseYearWithRedux(get().dreData);
      
      // 💾 Auto-save após remover ano
      get().saveDRE();
    },

    // ========================================================================
    // SALVAMENTO
    // ========================================================================

    saveDRE: async () => {
      const state = get();
      if (!state.dreData) return;

      set({ isSaving: true });

      try {
        // 🔴 MODO OFFLINE: Apenas mostrar mensagem, não salvar no Supabase
        if (OFFLINE_MODE) {
          console.log('[dreStore] 🔴 OFFLINE MODE: Simulando salvamento...');
          toast.success('✅ DRE salva localmente (modo offline)');
          
          // Trigger recálculo de valuation mesmo em offline
          get().recalculateAll();
          
          set({ isSaving: false });
          return;
        }

        const { data: { user } } = await supabase.auth.getUser();
        
        // 🧪 MODO DEV: Usar user fake se não estiver autenticado
        let effectiveUser: { id: string } | null = user;
        
        if (!user && DEV_MODE_ALLOW_UNAUTHENTICATED) {
          console.warn('[dreStore] 🧪 DEV MODE: Usando user_id fake para testes');
          effectiveUser = { id: DEV_USER_ID };
        }
        
        if (!effectiveUser) {
          toast.error('Você precisa estar logado para salvar');
          set({ isSaving: false });
          return;
        }

        // 🔍 Verificar se já existe um registro no banco para este usuário
        const { data: existingData } = await supabase
          .from('dre_data')
          .select('id')
          .eq('user_id', effectiveUser.id)
          .maybeSingle();

        console.log('[dreStore] 💾 Salvando DRE. Registro existente:', existingData?.id || 'nenhum');

        // � Preparar dados para compressão
        const dataToCompress = {
          setima_years: state.dreData.setima.years,
          studio555_years: state.dreData.studio555.years,
        };

        // 📊 Log de estatísticas de compressão
        const stats = getCompressionStats(dataToCompress);
        console.log('[dreStore] 📊 Compressão:',
          `Original: ${stats.originalKB} KB →`,
          `Comprimido: ${stats.compressedKB} KB`,
          `(redução de ${stats.reductionPercent}%)`
        );

        // 🗜️ Comprimir dados
        const compressed = compressData(dataToCompress);

        // 📝 Preparar payload com dados comprimidos
        const payload: {
          id?: string;
          user_id: string;
          data_compressed: string;
          updated_at: string;
        } = {
          user_id: effectiveUser.id,
          data_compressed: compressed,
          updated_at: new Date().toISOString(),
        };

        // ✅ Se já existe no banco, incluir o ID do banco para UPDATE
        if (existingData?.id) {
          payload.id = existingData.id;
        }

        const { error } = await supabase
          .from('dre_data')
          .upsert(payload, { onConflict: 'user_id' });

        if (error) throw error;

        // 🔄 Atualizar o ID local com o ID do banco (caso tenha sido criado agora)
        if (!existingData?.id) {
          const { data: newData } = await supabase
            .from('dre_data')
            .select('id')
            .eq('user_id', effectiveUser.id)
            .single();
          
          if (newData) {
            set({ dreData: { ...state.dreData, id: newData.id } });
            console.log('[dreStore] ✅ ID atualizado no estado:', newData.id);
          }
        }

        toast.success('DRE salva com sucesso');
        
        // Trigger recálculo de valuation
        get().recalculateAll();
        
      } catch (error) {
        console.error('Erro ao salvar DRE:', error);
        toast.error('Erro ao salvar DRE');
      } finally {
        set({ isSaving: false });
      }
    },

    // ========================================================================
    // RECÁLCULO
    // ========================================================================

    /**
     * Recalcula todos os campos derivados da DRE e propaga para valuation
     * 
     * Ações:
     * 1. Recalcula campos calculados de todos os anos
     * 2. Valida integridade dos dados
     * 3. Notifica outros componentes para recalcular
     * 4. TODO: Integrar com ValuationEngine para recalcular premissas/projeções
     */
    recalculateAll: () => {
      console.log('[dreStore] 🔄 recalculateAll() INICIADO');
      
      const state = get();
      if (!state.dreData) {
        console.warn('[dreStore] ⚠️ Nenhuma DRE carregada para recalcular');
        return;
      }

      set((draft) => {
        if (!draft.dreData) return;

        // Recalcular Sétima
        draft.dreData.setima.years = draft.dreData.setima.years.map(recalculateDREYear);
        console.log('[dreStore] ✅ Sétima recalculada:', draft.dreData.setima.years.length, 'anos');

        // Recalcular 555 Studios
        draft.dreData.studio555.years = draft.dreData.studio555.years.map(recalculateDREYear);
        console.log('[dreStore] ✅ 555 Studios recalculada:', draft.dreData.studio555.years.length, 'anos');

        // Atualizar timestamp
        draft.dreData.updatedAt = new Date();
      });

      // Validar após recálculo
      get().validate();

      // TODO: Integrar com ValuationEngine
      // - Recalcular premissas (margens target, crescimento médio, etc.)
      // - Recalcular projeções (FCFF 2025-2029)
      // - Recalcular WACC se necessário
      // - Recalcular Enterprise Value
      // - Recalcular cenários (Pessimista, Base, Otimista)
      // - Recalcular análise de sensibilidade
      
      console.log('[dreStore] 🔄 recalculateAll() CONCLUÍDO');
      
      // Notificação visual
      if (state.validationState?.isValid) {
        toast.success('✅ DRE recalculada com sucesso');
      } else {
        toast.error('⚠️ DRE recalculada com avisos de validação');
      }
    },

    // ========================================================================
    // VALIDAÇÃO
    // ========================================================================

    validate: () => {
      const state = get();
      if (!state.dreData) return;

      const setimaValidation = validateDREYears(state.dreData.setima.years, 'setima');
      const studio555Validation = validateDREYears(state.dreData.studio555.years, '555');

      const combinedValidation: ValidationState = {
        isValid: setimaValidation.isValid && studio555Validation.isValid,
        errors: [...setimaValidation.errors, ...studio555Validation.errors],
        warnings: [...setimaValidation.warnings, ...studio555Validation.warnings],
        infos: [...setimaValidation.infos, ...studio555Validation.infos],
      };

      set({ validationState: combinedValidation });
    },

    // ========================================================================
    // UNDO/REDO
    // ========================================================================

    undo: () => {
      const previousState = useUndoRedoStore.getState().undo();
      if (previousState) {
        set({ dreData: previousState });
        toast.success('Alteração desfeita');
        get().validate();
      }
    },

    redo: () => {
      const nextState = useUndoRedoStore.getState().redo();
      if (nextState) {
        set({ dreData: nextState });
        toast.success('Alteração refeita');
        get().validate();
      }
    },

    canUndo: () => useUndoRedoStore.getState().canUndo(),

    canRedo: () => useUndoRedoStore.getState().canRedo(),

    // ========================================================================
    // RESET
    // ========================================================================

    reset: () => {
      set({
        dreData: null,
        isEditing: false,
        isLoading: false,
        isSaving: false,
        validationState: null,
      });
      useUndoRedoStore.getState().clear();
    },

    /**
     * Restaura DRE para dados originais (MASTER_DRE_DATA)
     * Útil para resetar todas as alterações e voltar ao histórico base
     */
    restoreToOriginal: () => {
      const { dreData } = get();
      
      if (!dreData) {
        toast.error('Nenhuma DRE carregada');
        return;
      }

      // Salva estado atual no undo antes de resetar
      useUndoRedoStore.getState().pushAction(dreData, 'Restaurou para dados originais');

      // Cria nova DRE com dados master mas mantém IDs
      const restoredDRE: DREData = {
        ...MASTER_DRE_DATA,
        id: dreData.id,
        userId: dreData.userId,
        createdAt: dreData.createdAt,
        updatedAt: new Date(),
      };

      set({ dreData: restoredDRE });
      get().validate();
      
      toast.success('DRE restaurada para dados originais do histórico', {
        duration: 4000,
        icon: '',
      });

      console.log('[dreStore] ✅ DRE restaurada para MASTER_DRE_DATA');
    },
  }))
);
